import React, { useCallback, useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { useAgentSkills } from 'data/hooks/agents/skills'
import { AgentSkill, SkillInput } from 'data/hooks/agents/types'
import { EditSelectedModel } from 'features/Agents/configuration/common/EditSelectedModel'
import { convertNameToApiName } from 'features/Agents/configuration/common/utils'
import { EditTools } from 'features/Agents/configuration/tools/EditTools'
import { OutputSchemaEditor } from 'features/workflows/OutputSchema/OutputSchemaEditor'

import useDebounce from 'v2/ui/utils/useDebounce'
import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { ColorPicker } from 'ui/components/ColorPicker/ColorPicker'
import { Field } from 'ui/components/Field'
import { Icon } from 'ui/components/Icon'
import { IconPickerDropdown } from 'ui/components/IconPicker/IconPickerDropdown'
import { Input } from 'ui/components/Input'
import { Select, SelectOption } from 'ui/components/Select'
import { Body } from 'ui/components/Text'
import { Textarea } from 'ui/components/Textarea'
import { useToast } from 'ui/components/Toast'

import { EditEnabledSkills } from './EditEnabledSkills'

const SET_VALUE_DEBOUNCE_MS = 100

export function EditSkillForm({ agentSid, skill }: { agentSid: string; skill?: AgentSkill }) {
    const { register, setValue } = useFormContext<Partial<AgentSkill>>()
    const { data: skills = [] } = useAgentSkills(agentSid)

    const options = useWatch({ name: 'options' }) || {}
    const filteredSkills = useMemo(() => {
        return skills.filter((s) => s.api_name !== skill?.api_name)
    }, [skills, skill])
    const apiName = useWatch({ name: 'api_name' })
    const name = useWatch({ name: 'name' })

    const toast = useToast()
    function copyApiName() {
        if (!apiName) return
        navigator.clipboard.writeText(apiName)
        toast({ title: 'Id copied to clipboard', type: 'success' })
    }

    const debouncedSetValue = useDebounce(setValue, SET_VALUE_DEBOUNCE_MS, [setValue], {
        leading: true,
    })

    useEffect(() => {
        if (!name) return

        debouncedSetValue('api_name', convertNameToApiName(name), { shouldDirty: true })
    }, [name, debouncedSetValue])

    const allReservedApiNames = [...skills.map((s) => s.api_name), apiName].filter(
        Boolean
    ) as string[]
    const reservedApiNames = useDeepEqualsMemoValue(allReservedApiNames)

    return (
        <Box as="form" flex gap="l" flexGrow={1}>
            <Box flex grow column gap="l">
                <Box flex center gap="l">
                    <Field required label="Skill Name" width="full" grow>
                        <Box flex gap="m">
                            <IconPickerDropdown
                                value={options?.icon || { name: 'Zap', type: 'lucide' }}
                                onChange={(newIcon) =>
                                    setValue('options.icon', newIcon, { shouldDirty: true })
                                }
                            />
                            <ColorPicker
                                value={options?.color || 'sky'}
                                onChange={(newColor) =>
                                    setValue('options.color', newColor, { shouldDirty: true })
                                }
                            />
                            <Input
                                placeholder="Enter skill name"
                                {...register('name', { required: true })}
                            />
                        </Box>
                    </Field>
                    <Field
                        label="Skill Id"
                        shrink
                        style={{ width: 'auto' }}
                        rightSlotContent={
                            <Button
                                variant="ghost"
                                size="xs"
                                onClick={copyApiName}
                                iconOnly
                                startIcon={{ name: 'Copy' }}
                            />
                        }
                    >
                        <Input readOnly {...register('api_name', { required: true })} />
                    </Field>
                </Box>

                <Field label="Description" width="5xl" shrink>
                    <Input placeholder="Enter skill description" {...register('description')} />
                </Field>
                <Textarea
                    required
                    flex
                    grow
                    label="Prompt"
                    placeholder="Enter skill prompt"
                    {...register('prompt', { required: true })}
                    fieldProps={{ style: { display: 'flex', flexDirection: 'column' } }}
                    style={{ display: 'flex', flexGrow: '1' }}
                />
            </Box>

            <Box flex style={{ width: '300px' }} column gap="l">
                <EditSkillInputs />
                <EditSkillOutputSchema />

                <EditEnabledSkills agentSid={agentSid} preventEdit skills={filteredSkills} />

                <EditTools agentSid={agentSid} reservedApiNames={reservedApiNames} />
                <EditSkillModel />
            </Box>
        </Box>
    )
}

function EditSkillModel() {
    const { setValue } = useFormContext<Partial<AgentSkill>>()
    const selectedModel = useWatch({ name: 'selected_model' })
    return (
        <EditSelectedModel
            value={selectedModel}
            onChange={(value) => setValue('selected_model', value, { shouldDirty: true })}
        />
    )
}

function EditSkillOutputSchema() {
    const { setValue } = useFormContext<Partial<AgentSkill>>()

    const outputSchema = useWatch({ name: 'output_schema' })
    const format = !!outputSchema ? 'json' : 'text'
    return (
        <Field label="Output" width="5xl" shrink>
            <Select
                value={format}
                onChange={(value) =>
                    value === 'json'
                        ? setValue('output_schema', { schema: [] }, { shouldDirty: true })
                        : setValue('output_schema', undefined, { shouldDirty: true })
                }
                size="m"
            >
                <SelectOption value="json" label="Structured" />
                <SelectOption value="text" label="Text" />
            </Select>
            {format === 'json' && (
                <OutputSchemaEditor
                    value={outputSchema}
                    onChange={(value) => setValue('output_schema', value, { shouldDirty: true })}
                    mt="l"
                />
            )}
        </Field>
    )
}
function EditSkillInputs() {
    const { register, setValue } = useFormContext<Partial<AgentSkill>>()

    const inputs: SkillInput[] | undefined = useWatch({ name: 'inputs' })
    const [openInputs, setOpenInputs] = React.useState<number[]>([])
    const toggleInput = (idx: number) => {
        setOpenInputs((prev) =>
            prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
        )
    }

    const removeInput = (idx: number) => {
        setValue(
            'inputs',
            inputs?.filter((_, i) => i !== idx),
            { shouldDirty: true }
        )
        setOpenInputs((prev) => prev.filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i)))
    }

    const fixInputNames = useCallback(() => {
        const newInputs: AgentSkill['inputs'] = []
        let madeChanges = false
        for (const input of inputs ?? []) {
            const name = convertNameToApiName(input.name)
            if (name !== input.name) {
                madeChanges = true
            }
            newInputs.push({
                ...input,
                name,
            })
        }
        if (madeChanges) {
            setValue('inputs', newInputs, { shouldDirty: true })
        }
    }, [inputs, setValue])

    return (
        <Field label="Inputs" shrink flex column gap="s">
            <Box flex column gap="s">
                {inputs?.map((input, idx) => (
                    <Box key={idx} flex column gap="s">
                        <Box
                            flex
                            center
                            gap="s"
                            onClick={() => toggleInput(idx)}
                            style={{ cursor: 'pointer' }}
                        >
                            <Box flex center grow gap="s">
                                <Box
                                    style={{
                                        transform: openInputs.includes(idx)
                                            ? 'rotate(90deg)'
                                            : 'none',
                                        transition: 'transform 0.2s',
                                    }}
                                    flex
                                >
                                    <Icon name="ChevronRight" />
                                </Box>
                                {openInputs.includes(idx) ? (
                                    <Body color="textWeaker">Configure input {idx + 1}</Body>
                                ) : (
                                    <Box>
                                        {input.name ? (
                                            <Body weight="bold">{input.name}</Body>
                                        ) : (
                                            <Body color="textWeaker">Name</Body>
                                        )}
                                        :{' '}
                                        {input.description ? (
                                            <Body>{input.description}</Body>
                                        ) : (
                                            <Body color="textWeaker">Description</Body>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            <Box
                                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                    e.stopPropagation()
                                    removeInput(idx)
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <Icon name="X" />
                            </Box>
                        </Box>

                        {openInputs.includes(idx) && (
                            <Box flex column gap="s" ml="l">
                                <Input
                                    size="m"
                                    {...register(`inputs.${idx}.name`)}
                                    placeholder="name"
                                    onBlur={() => fixInputNames()}
                                />
                                <Input
                                    size="m"
                                    {...register(`inputs.${idx}.description`)}
                                    placeholder="description"
                                />
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
            <Button
                mt="s"
                variant="secondary"
                size="s"
                startIcon={{ name: 'Plus' }}
                onClick={() => {
                    const newIdx = inputs?.length ?? 0
                    setValue('inputs', [...(inputs ?? []), { name: '', description: '' }], {
                        shouldDirty: true,
                    })
                    setOpenInputs((prev) => [...prev, newIdx])
                }}
            >
                Add input
            </Button>
        </Field>
    )
}
